import * as z from "zod";
import { userFacingError } from "../authorization";
import { normalizedConnectorIdentity } from "../connectors/normalizeUrlPattern";
import { hasAccess } from "../rbac/access";
import {
  grantCustomRoleResolve,
  grantPrincipalResolve,
} from "../rbac/grantResources";
import {
  assertRoleDoesNotGrant,
  grantEntry,
  insertGrantsDeduped,
  validateGrants,
} from "../rbac/validation";
import { creatorColumns } from "../rbac/roles";
import {
  createOAuthConnectionSchema,
  insertFreshOAuthConnection,
} from "./freshOAuthConnection";
import { mutator } from "./mutator";
import { generateId, stringRecord } from "./utils";

export const createConnectorSchema = z.object({
  connectedAppId: z.string(),
  name: z.string(),
  identifier: z.string(),
  urlPattern: z.string(),
  configuration: stringRecord,
  secrets: stringRecord,
  oauthConnectionId: z.string().optional(),
  createOAuthConnection: createOAuthConnectionSchema.optional(),
  grants: z.array(grantEntry).optional(),
});

export const createConnectedApp = mutator({
  redactKeys: ["secrets"],
  schema: createConnectorSchema,
  resolve: {
    oauthConnection: {
      kind: "oauthConnection",
      from: "oauthConnectionId",
      optional: true,
    },
    ...grantPrincipalResolve,
    ...grantCustomRoleResolve,
  },
  permissions: [
    {
      permission: "tenant:connected_app_create",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      connectedAppId,
      name,
      identifier,
      urlPattern,
      configuration,
      oauthConnectionId,
      createOAuthConnection,
      grants,
      grantCustomRoles,
    },
  }) => {
    const { userId } = ctx;
    const appId = connectedAppId;
    const oauthId =
      oauthConnectionId ?? (createOAuthConnection ? generateId() : null);

    const resolvedGrants = grants
      ? validateGrants(grants, "connected_app", grantCustomRoles)
      : [];

    if (createOAuthConnection && oauthId) {
      await insertFreshOAuthConnection(
        tx,
        createOAuthConnection,
        oauthId,
        ctx.tenantId
      );
    }

    const now = Date.now();
    await tx.mutate.connectedApps.insert({
      id: appId,
      ...normalizedConnectorIdentity({
        name,
        identifier,
        urlPattern,
        configuration,
      }),
      metadata: {},
      oauthConnectionId: oauthId,
      createdAt: now,
      ...creatorColumns(ctx),
      fresh: true,
      tenantId: ctx.tenantId,
    });

    if (grants) {
      // principal-exempt: reachable only via the browser /zero/mutate path and
      // the (session-only) createConnectedApp server mutator; ctx.principal is
      // never a service account here.
      const canShare = await hasAccess({
        tx,
        userId,
        scopeId: ctx.tenantId,
        tenantId: ctx.tenantId,
        permission: "tenant:connected_app_share",
      });
      if (!canShare) {
        for (const { input: grant, role } of resolvedGrants) {
          if (grant.userId !== userId) {
            throw userFacingError(
              "You don’t have permission to share connectors with others"
            );
          }
          assertRoleDoesNotGrant(role, "connected_app:share");
        }
      }
      await insertGrantsDeduped(tx, grants, appId, ctx.tenantId);
    }
  },
});
